import {
  isAnimationEasing,
  type AnimationEasing,
  type AnimationInterpolation,
  type AnimationLoopMode,
} from '@sharefe/share3d-engine'

export const CAMERA_ANIMATION_KEYFRAME_FORMAT = 'share3d-camera-animation'
export const CAMERA_ANIMATION_KEYFRAME_VERSION = 1
export const CAMERA_ANIMATION_FOV_MIN = 20
export const CAMERA_ANIMATION_FOV_MAX = 100
export const CAMERA_ANIMATION_MIN_KEYFRAME_GAP = 0.01

export type CameraAnimationEasingMode = 'none' | AnimationEasing

export interface CameraAnimationKeyframe {
  readonly time: number
  readonly position: readonly [number, number, number]
  readonly target: readonly [number, number, number]
  readonly fov: number
}

export interface CameraAnimationKeyframeFile {
  readonly format: typeof CAMERA_ANIMATION_KEYFRAME_FORMAT
  readonly version: typeof CAMERA_ANIMATION_KEYFRAME_VERSION
  readonly interpolationMode: AnimationInterpolation
  readonly easingMode: CameraAnimationEasingMode
  readonly loopMode: AnimationLoopMode
  readonly keyframes: readonly CameraAnimationKeyframe[]
}

export interface CameraAnimationKeyframeSource {
  readonly interpolationMode: AnimationInterpolation
  readonly easingMode: CameraAnimationEasingMode
  readonly loopMode: AnimationLoopMode
  readonly keyframes: readonly CameraAnimationKeyframe[]
}

const INTERPOLATION_MODES: readonly AnimationInterpolation[] = ['step', 'linear', 'spline']
const LOOP_MODES: readonly AnimationLoopMode[] = ['once', 'repeat', 'pingpong']

/** 组装可导出的关键帧文件；导出前统一排序、取整并夹取 FOV。 */
export function createCameraAnimationKeyframeFile(
  source: CameraAnimationKeyframeSource
): CameraAnimationKeyframeFile {
  return {
    format: CAMERA_ANIMATION_KEYFRAME_FORMAT,
    version: CAMERA_ANIMATION_KEYFRAME_VERSION,
    interpolationMode: source.interpolationMode,
    easingMode: source.easingMode,
    loopMode: source.loopMode,
    keyframes: normalizeKeyframes(source.keyframes),
  }
}

export function stringifyCameraAnimationKeyframeFile(file: CameraAnimationKeyframeFile): string {
  return `${JSON.stringify(file, null, 2)}\n`
}

/**
 * 解析关键帧文件并在解析阶段完成规范化。
 *
 * 文件来自用户手选，任何结构问题都必须给出可读错误，不能把脏数据写进播放轨道。
 */
export function parseCameraAnimationKeyframeFile(source: string): CameraAnimationKeyframeFile {
  let raw: unknown
  try {
    raw = JSON.parse(source)
  } catch (error) {
    throw new Error(`JSON 解析失败：${error instanceof Error ? error.message : String(error)}`)
  }

  if (!isRecord(raw)) {
    throw new Error('关键帧文件必须是 JSON 对象。')
  }
  if (raw.format !== CAMERA_ANIMATION_KEYFRAME_FORMAT) {
    throw new Error('不是本示例导出的关键帧文件。')
  }
  if (raw.version !== CAMERA_ANIMATION_KEYFRAME_VERSION) {
    throw new Error(`不支持的关键帧文件版本：${String(raw.version)}`)
  }

  const interpolationMode = raw.interpolationMode === undefined
    ? 'linear'
    : readInterpolationMode(raw.interpolationMode)
  const easingMode = raw.easingMode === undefined ? 'none' : readEasingMode(raw.easingMode)
  const loopMode = raw.loopMode === undefined ? 'once' : readLoopMode(raw.loopMode)

  if (!Array.isArray(raw.keyframes)) {
    throw new Error('关键帧文件的 keyframes 必须是数组。')
  }
  if (raw.keyframes.length === 0) {
    throw new Error('关键帧文件至少需要一帧。')
  }

  const keyframes = normalizeKeyframes(
    raw.keyframes.map((frame, index) => readKeyframe(frame, index))
  )

  return {
    format: CAMERA_ANIMATION_KEYFRAME_FORMAT,
    version: CAMERA_ANIMATION_KEYFRAME_VERSION,
    interpolationMode,
    easingMode,
    loopMode,
    keyframes,
  }
}

function readKeyframe(value: unknown, index: number): CameraAnimationKeyframe {
  const label = `第 ${index + 1} 帧`
  if (!isRecord(value)) {
    throw new Error(`${label}必须是对象。`)
  }

  return {
    time: readNumber(value.time, `${label}的 time`),
    position: readVector(value.position, `${label}的 position`),
    target: readVector(value.target, `${label}的 target`),
    fov: readNumber(value.fov, `${label}的 fov`),
  }
}

function normalizeKeyframes(
  keyframes: readonly CameraAnimationKeyframe[]
): CameraAnimationKeyframe[] {
  const sorted = [...keyframes].sort((a, b) => a.time - b.time)
  const normalized: CameraAnimationKeyframe[] = []

  for (const frame of sorted) {
    const previous = normalized[normalized.length - 1]
    const minimumTime = previous ? previous.time + CAMERA_ANIMATION_MIN_KEYFRAME_GAP : 0
    const time = round(Math.max(frame.time, minimumTime), 3)
    normalized.push({
      time,
      position: [round(frame.position[0], 3), round(frame.position[1], 3), round(frame.position[2], 3)],
      target: [round(frame.target[0], 3), round(frame.target[1], 3), round(frame.target[2], 3)],
      fov: round(clamp(frame.fov, CAMERA_ANIMATION_FOV_MIN, CAMERA_ANIMATION_FOV_MAX), 1),
    })
  }

  return normalized
}

function readInterpolationMode(value: unknown): AnimationInterpolation {
  if (typeof value === 'string' && (INTERPOLATION_MODES as readonly string[]).includes(value)) {
    return value as AnimationInterpolation
  }
  throw new Error(`插值方式无效：${String(value)}`)
}

function readEasingMode(value: unknown): CameraAnimationEasingMode {
  if (value === 'none' || isAnimationEasing(value)) {
    return value
  }
  throw new Error(`缓动函数无效：${String(value)}`)
}

function readLoopMode(value: unknown): AnimationLoopMode {
  if (typeof value === 'string' && (LOOP_MODES as readonly string[]).includes(value)) {
    return value as AnimationLoopMode
  }
  throw new Error(`循环模式无效：${String(value)}`)
}

function readNumber(value: unknown, label: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`${label} 必须是有限数字。`)
  }
  if (value < 0) {
    throw new Error(`${label} 不能为负数。`)
  }
  return value
}

function readVector(value: unknown, label: string): [number, number, number] {
  if (!Array.isArray(value) || value.length !== 3) {
    throw new Error(`${label} 必须是长度为 3 的数组。`)
  }
  const [x, y, z] = value
  if (typeof x !== 'number' || typeof y !== 'number' || typeof z !== 'number') {
    throw new Error(`${label} 的分量必须是数字。`)
  }
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
    throw new Error(`${label} 的分量必须是有限数字。`)
  }
  return [x, y, z]
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function round(value: number, digits: number): number {
  const factor = 10 ** digits
  return Math.round(value * factor) / factor
}
